import { useCanvasFileDrop } from '@block-canvas/signal/fileDrop';
import { useRenderState } from '@block-canvas/store/RenderState';
import { vec2 } from '@block-canvas/util/vector2';
import { EntityIcon } from '@core/component/EntityIcon';
import { FileDropOverlay } from '@core/component/FileDropOverlay';
import {
  blockAcceptsFileExtension,
  blockNameToFileExtensions,
  blockNameToMimeTypes,
} from '@core/constant/allBlocks';
import {
  ENABLE_CANVAS_HEIC,
  ENABLE_CANVAS_VIDEO,
} from '@core/constant/featureFlags';
import { fileDrop } from '@core/directive/fileDrop';
import { fileSelector } from '@core/directive/fileSelector';
import { HEIC_EXTENSIONS, HEIC_MIME_TYPES } from '@core/heic/constants';
import UploadSimple from '@phosphor-icons/core/bold/upload-simple-bold.svg?component-solid';
import Image from '@phosphor-icons/core/regular/image.svg?component-solid';
//import { copiedFile } from "@core/state/clipboard";
import { useHistoryQuery } from '@queries/history/history';
import { Dropdown } from '@ui';
import { createMemo, createSignal, Show } from 'solid-js';
import { VList } from 'virtua/solid';
import { Tools } from '../constants';
import { useCanvasDocument } from '../context/canvas-document-context';
import { useSelect } from '../operation/select';
import { useToolManager } from '../signal/toolManager';

false && fileSelector;
false && fileDrop;

type MediaType = 'image' | 'video';

type MediaItem = {
  fileName: string;
  fileType: MediaType;
  id: string;
};

function ItemOption(props: { media: MediaItem }) {
  const [, setSelectedImage] = useCanvasDocument().state.signals.selectedImage;
  const toolManager = useToolManager();
  const select = useSelect();

  const selectItem = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    select.abort();
    setSelectedImage({
      type: props.media.fileType,
      id: props.media.id,
    });
    toolManager.setSelectedTool(Tools.Image);
  };

  return (
    <div
      class="w-72 flex flex-row rounded hover:bg-hover hover-transition-bg p-2 text-sm select-none items-center"
      onmousedown={selectItem}
      ontouchstart={selectItem}
    >
      <EntityIcon targetType={props.media.fileType} size="sm" />
      <div class="line-clamp-1 overflow-hidden text-ellipsis ml-2">
        {props.media.fileName}
      </div>
    </div>
  );
}

export function MediaSelector() {
  const mediaTypes: MediaType[] = ENABLE_CANVAS_VIDEO
    ? ['image', 'video']
    : ['image'];
  const historyQuery = useHistoryQuery();
  //const copiedFileID = copiedFile();
  const select = useSelect();
  const { handleFileDrop } = useCanvasFileDrop();
  const [isDragging, setIsDragging] =
    useCanvasDocument().state.signals.canvasDragging;
  const { viewBox } = useRenderState();
  const centerVec = createMemo(() => {
    return vec2(viewBox().x + viewBox().w / 2, viewBox().y + viewBox().h / 2);
  });
  const { focusCanvas } = useToolManager();

  const [imageSelectorOpen, setImageSelectorOpen] = createSignal(false);

  const imageExtensions = blockNameToFileExtensions.image;
  const imageMimeTypes = blockNameToMimeTypes.image;
  const videoExtensions = blockNameToFileExtensions.video;
  const videoMimeTypes = blockNameToMimeTypes.video;

  const canvasImageExtensions = ENABLE_CANVAS_HEIC
    ? [...imageExtensions, ...HEIC_EXTENSIONS]
    : imageExtensions;
  const canvasImageMimeTypes = ENABLE_CANVAS_HEIC
    ? [...imageMimeTypes, ...HEIC_MIME_TYPES]
    : imageMimeTypes;

  const acceptedMimeTypes = ENABLE_CANVAS_VIDEO
    ? [...canvasImageMimeTypes, ...videoMimeTypes]
    : canvasImageMimeTypes;
  const acceptedFileExtensions = ENABLE_CANVAS_VIDEO
    ? [...canvasImageExtensions, ...videoExtensions]
    : canvasImageExtensions;

  const userMediaFiles = createMemo(() => {
    const mediaFiles: MediaItem[] = [];
    for (const item of historyQuery.data ?? []) {
      for (const mediaType of mediaTypes) {
        if (
          item.type === 'document' &&
          item.fileType &&
          blockAcceptsFileExtension(mediaType, item.fileType)
        ) {
          mediaFiles.push({
            fileName: item.name,
            fileType: mediaType,
            id: item.id,
          });
        }
      }
    }

    return mediaFiles;
  });

  return (
    <Dropdown open={imageSelectorOpen()} onOpenChange={setImageSelectorOpen}>
      <Dropdown.Trigger
        variant="ghost"
        size="icon-sm"
        label="Media"
        tabIndex={-1}
      >
        <Image />
      </Dropdown.Trigger>
      <Dropdown.Content onCloseAutoFocus={focusCanvas}>
        <Dropdown.Group>
          <div
            use:fileDrop={{
              acceptedMimeTypes: acceptedMimeTypes,
              acceptedFileExtensions: acceptedFileExtensions,
              onDragStart: () => setIsDragging(true),
              onDragEnd: () => setIsDragging(false),
              onDrop: (files) => {
                handleFileDrop(files, centerVec());
                setImageSelectorOpen(false);
              },
            }}
            class="flex flex-col gap-1"
          >
            <Show when={isDragging()}>
              <FileDropOverlay valid={true}>
                Drop any file here to add it to your canvas
              </FileDropOverlay>
            </Show>
            <Dropdown.Item closeOnSelect={false}>
              <div
                class="w-72 flex flex-row select-none items-center gap-1"
                onmousedown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  select.abort();
                }}
                ontouchstart={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  select.abort();
                }}
              >
                <div
                  class="w-full hidden sm:flex p-2 mb-1 bg-edge hover:bg-accent/15 hover-transition-bg transition-colors items-center justify-center gap-2"
                  use:fileSelector={{
                    acceptedFileExtensions: acceptedFileExtensions,
                    acceptedMimeTypes: acceptedMimeTypes,
                    onSelect: (files: File[]) => {
                      handleFileDrop(files, centerVec());
                      setImageSelectorOpen(false);
                    },
                  }}
                >
                  <UploadSimple class="size-3.5 shrink-0 text-accent" />
                  <span class="text-sm font-medium text-accent">
                    Upload File
                  </span>
                </div>
              </div>
            </Dropdown.Item>
            <div class="w-full">
              <Show
                when={userMediaFiles().length > 0}
                fallback={
                  <div class="p-4 text-sm text-center">No media found.</div>
                }
              >
                <VList
                  data={userMediaFiles()}
                  style={{ height: '256px' }}
                  bufferSize={500}
                >
                  {(media) => (
                    <Dropdown.Item closeOnSelect={true} class="w-full">
                      <ItemOption media={media} />
                    </Dropdown.Item>
                  )}
                </VList>
              </Show>
              {/* <Show when={copiedFile()}>
                      <DropdownMenu.Item>
                        <ItemOption image={{fileName:"Insert from clipboard", id:copiedFile()!}} />
                      </DropdownMenu.Item>
                    </Show> */}
            </div>
          </div>
        </Dropdown.Group>
      </Dropdown.Content>
    </Dropdown>
  );
}
